import { userFacingError } from "../authorization";
import { branchRole } from "../branchRole";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { actorColumns, type AttributionCtx } from "../rbac/roles";
import type { Transaction } from "@rocicorp/zero";

// `approvedHeadCommitId` is the branch head before any merge from live: a peer's
// approval of exactly that commit lets a blocked author push it themselves.
export async function assertNotSelfApproval(
  tx: Transaction<Schema>,
  actorCtx: AttributionCtx,
  sourceBranchId: string,
  workflowId: string,
  approvedHeadCommitId?: string | null
) {
  const workflow = await tx.run(zql.workflows.where("id", workflowId).one());
  if (!workflow) return;
  const space = await tx.run(zql.spaces.where("id", workflow.spaceId).one());
  if (!space?.blockSelfApproval) return;

  const { userId } = actorColumns(actorCtx);
  if (!userId) {
    throw userFacingError(
      "This space requires a different user to push branches live, and a service account cannot be that user"
    );
  }
  const branch = await tx.run(
    zql.workflowBranches
      .where("id", sourceBranchId)
      .related("contributors", (c) => c.where("userId", userId))
      .one()
  );
  const isOwner =
    branch?.userId === userId &&
    branchRole({ id: sourceBranchId, workflow }) === "other";
  if (!isOwner && !branch?.contributors.length) return;
  if (approvedHeadCommitId) {
    const request = await tx.run(
      zql.pushLiveRequests
        .where("branchId", sourceBranchId)
        .where("commitId", approvedHeadCommitId)
        .where("approvedByUserId", "IS NOT", null)
        .one()
    );
    if (request) return;
  }
  throw userFacingError(
    "This space requires a different user to push branches live"
  );
}
